// Generates a sequence of exercises for a lesson by mixing exercise types.
// The mix is roughly Duolingo-like: lots of translation + multiple-choice,
// some listening, occasional word-bank and matching.

import { expandAlternatives } from './normalize.js';
import { imageMap, hasImage } from '../content/images.js';

// Croatian clitics and very short particles — bad blank targets in cloze
// because they're mostly grammatical glue, not vocabulary the learner is
// trying to acquire.
const CLOZE_CLITICS = ['je','sam','si','smo','ste','su','da','u','na','i','a','se','li'];

// Last-resort filler when the pool can't produce enough unique distractors.
const CLOZE_FALLBACK_FILLER = ['dan','noć','jutro','večer','molim','hvala'];

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistractors(pool, correct, count, key) {
  const correctVal = correct[key];
  const candidates = pool
    .filter((p) => p[key] && p[key] !== correctVal)
    .map((p) => p[key]);
  const unique = Array.from(new Set(candidates));
  return shuffle(unique).slice(0, count);
}

// Build an exercise object from an item.
function makeTranslateToHR(item, _pool, sourceLang) {
  return {
    type: 'translate',
    direction: 'toHR',
    prompt: item[sourceLang],
    accept: expandAlternatives(item.hr),
    item,
    speakText: item.hr,
  };
}

function makeTranslateFromHR(item, _pool, sourceLang) {
  return {
    type: 'translate',
    direction: 'fromHR',
    prompt: item.hr,
    accept: expandAlternatives(item[sourceLang]),
    item,
    speakText: item.hr,
  };
}

function makeMultipleChoiceToHR(item, pool, sourceLang) {
  const distractors = pickDistractors(pool, item, 3, 'hr');
  if (distractors.length < 1) return makeTranslateToHR(item, pool, sourceLang);
  const options = shuffle([item.hr, ...distractors]);
  return {
    type: 'choice',
    direction: 'toHR',
    prompt: item[sourceLang],
    correct: item.hr,
    options,
    item,
    speakText: item.hr,
  };
}

function makeMultipleChoiceFromHR(item, pool, sourceLang) {
  const distractors = pickDistractors(pool, item, 3, sourceLang);
  if (distractors.length < 1) return makeTranslateFromHR(item, pool, sourceLang);
  const options = shuffle([item[sourceLang], ...distractors]);
  return {
    type: 'choice',
    direction: 'fromHR',
    prompt: item.hr,
    correct: item[sourceLang],
    options,
    item,
    speakText: item.hr,
  };
}

function makeListen(item, _pool, sourceLang) {
  const wordCount = item.hr.trim().split(/\s+/).filter(Boolean).length;
  return {
    type: 'listen',
    mode: wordCount === 1 ? 'word' : 'sentence',
    prompt: item.hr,
    accept: expandAlternatives(item.hr),
    hint: item[sourceLang],
    item,
    speakText: item.hr,
  };
}

// Picks one cloze target token and 3 distractor tokens drawn from the pool.
// Returns null when item.hr is too short for a useful blank.
function makeCloze(item, pool, sourceLang) {
  const tokens = item.hr.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;

  // Prefer the longest non-clitic token as the blank target.
  const candidates = tokens
    .map((tok, idx) => ({ tok, idx, lower: tok.toLowerCase().replace(/[.,!?;:"']/g, '') }))
    .filter((t) => !CLOZE_CLITICS.includes(t.lower));
  const ranked = (candidates.length ? candidates : tokens.map((tok, idx) => ({ tok, idx, lower: tok.toLowerCase() })))
    .slice()
    .sort((a, b) => b.tok.length - a.tok.length);
  const target = ranked[0];
  const correct = target.tok.replace(/[.,!?;:"']/g, '');
  const correctLower = correct.toLowerCase();

  // Build distractors: pull first token from other pool items, dedupe vs correct.
  const poolDistractors = pickDistractors(pool, item, 6, 'hr')
    .map((s) => s.trim().split(/\s+/)[0])
    .map((s) => s.replace(/[.,!?;:"']/g, ''))
    .filter((s) => s && s.toLowerCase() !== correctLower);

  const filler = CLOZE_FALLBACK_FILLER
    .filter((w) => w.toLowerCase() !== correctLower);

  const distinct = Array.from(new Set([...poolDistractors, ...filler])).slice(0, 3);
  if (distinct.length < 3) return null;

  const promptWithBlank = tokens
    .map((tok, idx) => (idx === target.idx ? '___' : tok))
    .join(' ');

  return {
    type: 'cloze',
    promptWithBlank,
    fullSentence: item.hr,
    translation: item[sourceLang],
    correct,
    options: shuffle([correct, ...distinct.slice(0, 3)]),
    item,
    speakText: item.hr,
  };
}

// Smarter word-bank distractors. Returns null when the phrase is single-word.
// For long phrases (≥4 words), enables `hardMode` and skips distractors —
// the ordering challenge is already plenty.
function makeWordBank(item, pool, _sourceLang) {
  const words = item.hr.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;

  const hardMode = words.length >= 4;
  const correctLower = new Set(words.map((w) => w.toLowerCase()));

  let extras = [];
  if (!hardMode) {
    // Single-token candidates drawn from other pool items.
    const samples = shuffle(pool.filter((p) => p.hr !== item.hr)).slice(0, 6);
    const tokens = [];
    samples.forEach((p) => {
      p.hr.trim().split(/\s+/).filter(Boolean).forEach((tok) => {
        const clean = tok.replace(/[.,!?;:"']/g, '');
        if (!clean) return;
        if (correctLower.has(clean.toLowerCase())) return;
        // Reject distractors that are substrings of (or contain) any correct token.
        const collides = words.some((w) => {
          const a = w.toLowerCase();
          const b = clean.toLowerCase();
          return a.includes(b) || b.includes(a);
        });
        if (collides) return;
        tokens.push(clean);
      });
    });

    // Prefer distractors whose length is in the same range as correct tokens.
    const avgLen = words.reduce((s, w) => s + w.length, 0) / words.length;
    const ranked = Array.from(new Set(tokens))
      .map((t) => ({ t, score: Math.abs(t.length - avgLen) }))
      .sort((a, b) => a.score - b.score)
      .map((x) => x.t);

    const desired = Math.min(3, Math.max(2, ranked.length));
    extras = ranked.slice(0, desired);
  }

  const bank = shuffle([...words, ...extras]);
  return {
    type: 'wordbank',
    prompt: item[_sourceLang],
    correct: words,
    bank,
    item,
    speakText: item.hr,
    hardMode,
  };
}

function makeMatch(items) {
  if (items.length < 3) return null;
  const count = Math.min(6, Math.max(3, items.length));
  const subset = shuffle(items).slice(0, count);
  return {
    type: 'match',
    pairs: subset.map((it) => ({ hr: it.hr, source: 'will be set per render', item: it })),
    items: subset,
  };
}

// Image-choice: 2x2 grid of images, pick the one matching the spoken word.
// Returns null when the item or pool can't support a 4-tile grid.
function makeImageChoice(item, pool, _sourceLang) {
  if (!hasImage(item.hr)) return null;
  const otherCovered = pool.filter((p) => p.hr !== item.hr && hasImage(p.hr));
  if (otherCovered.length < 3) return null;
  const distractors = shuffle(otherCovered).slice(0, 3);
  const options = shuffle([
    { hr: item.hr, src: imageMap[item.hr] },
    ...distractors.map((d) => ({ hr: d.hr, src: imageMap[d.hr] })),
  ]);
  return {
    type: 'image-choice',
    speakText: item.hr,
    prompt: item.hr,
    correct: item.hr,
    options,
    item,
  };
}

// Speak-out-loud. Requires a mic; the caller passes `speakEnabled` into
// generateExercises based on settings + denial flag, so unauthorized
// rolls fall through to translate cleanly.
function makeSpeak(item, _pool, sourceLang) {
  if (!item?.hr) return null;
  const words = item.hr.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 6) return null;
  return {
    type: 'speak',
    prompt: item.hr,
    accept: expandAlternatives(item.hr),
    translation: item[sourceLang],
    item,
    speakText: item.hr,
  };
}

// Maps a random roll [0,1) onto an exercise maker, matching the spec
// in §4 of the implementation document. Returns the function to call.
function pickMakerForRoll(roll, toHRAlt, _fromHRAlt, opts) {
  if (roll < 0.22) return toHRAlt ? makeTranslateToHR : makeTranslateFromHR;
  if (roll < 0.40) return toHRAlt ? makeMultipleChoiceToHR : makeMultipleChoiceFromHR;
  if (roll < 0.55) return makeCloze;
  if (roll < 0.68) return makeListen;
  if (roll < 0.78) return makeWordBank;
  if (roll < 0.88) return makeImageChoice;
  return opts?.speakEnabled ? makeSpeak : (() => null);
}

export function generateExercises(items, { sourceLang, isReview, speakEnabled = false } = {}) {
  const pool = items;
  const exercises = [];

  // Lead with a Match for review lessons — quick warm-up.
  if (isReview && items.length >= 3) {
    const m = makeMatch(items);
    if (m) exercises.push(m);
  }

  // Non-review lessons cycle through items twice; review lessons once.
  const passes = isReview ? 1 : 2;
  const ordered = [];
  for (let p = 0; p < passes; p++) ordered.push(...shuffle(items));

  ordered.forEach((item, idx) => {
    // Alternate the direction parameter so toHR/fromHR get balanced coverage.
    const toHRAlt = idx % 2 === 0;
    let exercise = null;

    // Try up to 6 rolls before falling back. The maker may return null
    // (e.g. cloze refuses single-word items, wordbank refuses too) — in
    // which case we re-roll, and finally fall back to a translate.
    for (let tries = 0; tries < 6 && !exercise; tries++) {
      const roll = Math.random();
      const fn = pickMakerForRoll(roll, toHRAlt, !toHRAlt, { speakEnabled });
      exercise = fn(item, pool, sourceLang);
    }
    if (!exercise) exercise = makeTranslateToHR(item, pool, sourceLang);
    exercises.push(exercise);
  });

  // Insert a pairs/match refresh every ~4 exercises (skip for review since
  // it already starts with one).
  if (!isReview && items.length >= 3) {
    const insertEvery = 4;
    for (let i = insertEvery; i < exercises.length; i += insertEvery + 1) {
      const m = makeMatch(items);
      if (m) exercises.splice(i, 0, m);
    }
  }

  return exercises;
}
